import React, { useState, useEffect, useRef } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { 
  getStoredVersion, 
  setStoredVersion, 
  setReloadAttempted,
  shouldShowUpdateNotification,
} from '@/config/version';

interface VersionInfo {
  version_number: string;
  description: string | null;
  release_date: string;
}

export const VersionUpdateNotification: React.FC = () => {
  const { t } = useTranslation();
  const [currentVersion, setCurrentVersion] = useState<string>(getStoredVersion());
  const [latestVersion, setLatestVersion] = useState<VersionInfo | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const initialized = useRef(false);

  const checkForUpdates = async () => {
    try {
      const { data, error } = await supabase
        .from('version_history')
        .select('version_number, description, release_date')
        .eq('is_active', true)
        .single();

      if (error || !data) return;

      const storedVersion = getStoredVersion();
      setCurrentVersion(storedVersion);
      setLatestVersion(data);

      const shouldShow = shouldShowUpdateNotification(storedVersion, data.version_number);
      setIsVisible(shouldShow);
    } catch (error) {
      console.error('Error checking for updates:', error);
    }
  };

  const handleRefresh = () => {
    if (latestVersion) {
      setIsRefreshing(true);
      setReloadAttempted(latestVersion.version_number);
      setIsVisible(false);
      const url = new URL(window.location.href);
      url.searchParams.set('v', latestVersion.version_number);
      setTimeout(() => window.location.replace(url.toString()), 300);
    }
  };

  const handleDismiss = () => {
    if (latestVersion) setStoredVersion(latestVersion.version_number);
    setIsVisible(false);
  };

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    // Defer initial check by 5 seconds to not compete with critical rendering.
    // Then only re-check on visibility regain, with a 30-min minimum interval
    // (the 5-min setInterval was constant baseline egress per open tab).
    const MIN_INTERVAL_MS = 30 * 60_000;
    let lastCheck = 0;

    const maybeCheck = () => {
      const now = Date.now();
      if (now - lastCheck < MIN_INTERVAL_MS) return;
      lastCheck = now;
      checkForUpdates();
    };

    const initTimer = setTimeout(() => {
      lastCheck = Date.now();
      checkForUpdates();
    }, 5000);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') maybeCheck();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearTimeout(initTimer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  useEffect(() => {
    if (latestVersion && currentVersion) {
      const shouldShow = shouldShowUpdateNotification(currentVersion, latestVersion.version_number);
      setIsVisible(shouldShow);
    }
  }, [latestVersion, currentVersion]);

  if (!isVisible || !latestVersion) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[2200] max-w-sm">
      <Card className="shadow-lg border-primary bg-background">
        <CardContent className="p-4">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-primary" />
              <span className="font-semibold text-sm flex-1">
                {t('version.updateAvailable', 'New Version Available')}
              </span>
              <button
                type="button"
                onClick={handleDismiss}
                aria-label="Close"
                className="rounded-md p-1 text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-sm text-muted-foreground">
              {t('version.updateMessage', 'Version {{version}} is now available.', {
                version: latestVersion.version_number
              })}
            </p>
            {latestVersion.description && (
              <p className="text-xs text-muted-foreground">
                {latestVersion.description}
              </p>
            )}
            <Button onClick={handleRefresh} disabled={isRefreshing} className="w-full">
              {isRefreshing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  {t('version.refreshing', 'Refreshing...')}
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {t('version.refresh', 'Refresh')}
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
